/**
 * Page /mon-abonnement — détail de l'abonnement courant et actions.
 *
 * - Affiche le statut (tier, période, expiration)
 * - Bouton "Gérer ma CB" qui ouvre le Stripe Customer Portal
 * - Bouton "Changer de formule" qui renvoie sur /abonnement
 *
 * Sur Capacitor natif, le bouton de gestion ouvre le portail dans le
 * navigateur système (pas dans la webview de l'app) pour conformité stores.
 */

import { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../hooks/useSubscription';
import { openCustomerPortal } from '../lib/billingApi';
import { isNative, openExternalUrl } from '../platform/capacitor';
import type { Session } from '../types/app';
import '../styles/subscription.css';

interface MySubscriptionProps {
  session: Session;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Actif',
  trialing: 'Essai gratuit',
  past_due: 'Paiement en attente',
  canceled: 'Annulé',
  unpaid: 'Impayé',
  paused: 'En pause',
  incomplete: 'Incomplet',
  incomplete_expired: 'Expiré',
  expired: 'Expiré',
};

const TIER_LABELS: Record<string, string> = {
  free: 'Détente gratuit',
  classic_plus: 'Détente Plus',
  competition: 'Espace Compétition',
};

const PROVIDER_LABELS: Record<string, string> = {
  stripe: 'Carte bancaire (Stripe)',
  cmcic_legacy: 'Carte bancaire (système historique)',
  apple_iap: 'App Store',
  google_iap: 'Google Play',
};

export default function MySubscription({ session }: MySubscriptionProps) {
  const { setActiveTab } = useApp();
  const { subscription, loading, refresh, isTrial, daysUntilExpiry } = useSubscription(
    session.pseudo, session.token,
  );
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const native = isNative();

  const handlePortal = async () => {
    if (!session.token) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { url } = await openCustomerPortal(session.token);
      if (native) {
        await openExternalUrl(url);
      } else {
        window.location.href = url;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPortalError(`Impossible d'ouvrir le portail : ${msg}`);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading && !subscription) {
    return (
      <div className="subscription-page">
        <div className="my-subscription-empty">Chargement…</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-page">
        <header className="subscription-header">
          <h1 className="subscription-title">Mon abonnement</h1>
        </header>
        <div className="my-subscription-card">
          <div className="my-subscription-empty">
            <p>Vous n'avez pas encore d'abonnement actif.</p>
            {!native && (
              <button
                type="button"
                className="my-subscription-empty-cta"
                onClick={() => setActiveTab('subscription')}
              >
                Découvrir les abonnements
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const sub = subscription;
  const willCancel = sub.cancelAt && new Date(sub.cancelAt) > new Date();

  return (
    <div className="subscription-page">
      <header className="subscription-header">
        <h1 className="subscription-title">Mon abonnement</h1>
        <p className="subscription-subtitle">
          Gérez votre abonnement webtarot.
        </p>
      </header>

      {/* Bandeau d'alerte si essai ou bientôt annulé */}
      {isTrial && (
        <div className="subscription-banner trial">
          <div className="subscription-banner-text">
            <strong>Essai gratuit en cours</strong>
            <small>
              Votre essai expire dans {daysUntilExpiry} jour{daysUntilExpiry === 1 ? '' : 's'}.
              La carte bancaire renseignée sera débitée automatiquement à l'issue de l'essai.
            </small>
          </div>
        </div>
      )}
      {willCancel && (
        <div className="subscription-banner warning">
          <div className="subscription-banner-text">
            <strong>Abonnement programmé pour annulation</strong>
            <small>
              Votre abonnement prendra fin le {formatDate(sub.cancelAt!)}.
              Vous gardez votre accès jusqu'à cette date.
            </small>
          </div>
        </div>
      )}

      <div className="my-subscription-card">
        <div className="my-subscription-row">
          <span className="my-subscription-row-label">Formule</span>
          <span className="my-subscription-row-value">{TIER_LABELS[sub.tier] || sub.tier}</span>
        </div>
        <div className="my-subscription-row">
          <span className="my-subscription-row-label">Statut</span>
          <span className="my-subscription-row-value">{STATUS_LABELS[sub.status] || sub.status}</span>
        </div>
        <div className="my-subscription-row">
          <span className="my-subscription-row-label">Période en cours</span>
          <span className="my-subscription-row-value">
            du {formatDate(sub.currentPeriodStart)} au {formatDate(sub.currentPeriodEnd)}
          </span>
        </div>
        {sub.trialEnd && (
          <div className="my-subscription-row">
            <span className="my-subscription-row-label">Fin de l'essai</span>
            <span className="my-subscription-row-value">{formatDate(sub.trialEnd)}</span>
          </div>
        )}
        <div className="my-subscription-row">
          <span className="my-subscription-row-label">Moyen de paiement</span>
          <span className="my-subscription-row-value">
            {PROVIDER_LABELS[sub.provider] || sub.provider}
          </span>
        </div>

        {portalError && (
          <div className="my-subscription-row" style={{ color: '#ef4444' }}>
            <span>{portalError}</span>
          </div>
        )}

        <div className="my-subscription-actions">
          {sub.provider === 'stripe' ? (
            <button
              type="button"
              onClick={handlePortal}
              disabled={portalLoading}
            >
              {portalLoading ? 'Ouverture…' : 'Gérer ma carte bancaire'}
            </button>
          ) : (
            <button type="button" disabled title="Ce moyen de paiement n'est pas géré ici.">
              Géré en externe
            </button>
          )}
          <button
            type="button"
            className="secondary"
            onClick={() => setActiveTab('subscription')}
          >
            Changer de formule
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void refresh()}
            disabled={loading}
          >
            Rafraîchir
          </button>
        </div>
      </div>
    </div>
  );
}
